import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { StatusBadge } from "../components/StatusBadge";
import { ComprobanteModal } from "../components/ComprobanteModal";
import { ConfirmFlete } from "../components/ConfirmFlete";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { formatGs, STATUS_META, waLink, avisoEntregaRemitente, pyToday } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Store, Truck, UserPlus, PackageCheck, Ship, PackageSearch, MessageCircle, BadgeDollarSign, AlertTriangle } from "lucide-react";
import { IncidenciaModal } from "../components/IncidenciaModal";
import { ResolverIncidenciaModal } from "../components/ResolverIncidenciaModal";
import { GastoModal } from "../components/GastoModal";
import { toast } from "sonner";

const NEXT_STATUS = {
  solicitado: "retirado", retirado: "depositado_origen", depositado_origen: "en_transito",
  en_transito: "en_sucursal_destino",
  en_sucursal_destino: "asignado_repartidor", asignado_repartidor: "en_reparto", en_reparto: "entregado",
};

export default function GestionarSucursal() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const isPunto = user?.role === "punto";
  const [suc, setSuc] = useState(user?.sucursal || "ASU");
  const [envios, setEnvios] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [repartidores, setRepartidores] = useState([]);
  const [assign, setAssign] = useState(null); // { type: "envio"|"retiro", item }
  const [comp, setComp] = useState(null);
  const [selected, setSelected] = useState({}); // id -> true
  const [bulkLoading, setBulkLoading] = useState(false);
  const [avisar, setAvisar] = useState([]); // envíos desembarcados a avisar por WhatsApp
  const [cf, setCf] = useState(null); // confirmar flete
  const [inc, setInc] = useState(null); // reportar incidencia
  const [resol, setResol] = useState(null); // resolver incidencia
  const [showGasto, setShowGasto] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selRet, setSelRet] = useState({}); // retiro id -> true
  const selRetIds = Object.keys(selRet).filter((k) => selRet[k]);
  const toggleRet = (id) => setSelRet((s) => ({ ...s, [id]: !s[id] }));
  const isAdmin = user?.role === "admin";
  const puntoCodes = branches.filter((b) => b.es_punto).map((b) => b.code);
  const isPuntoBranch = (code) => puntoCodes.includes(code);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const bulk = async (action) => {
    if (!selectedIds.length) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/envios/bulk-status", { ids: selectedIds, action });
      toast.success(`${data.updated} envíos actualizados → ${STATUS_META[data.status]?.label || data.status}${data.skipped ? ` · ${data.skipped} omitidos (estado no corresponde)` : ""}`);
      if (action === "desembarcar" && data.envios?.length) setAvisar(data.envios);
      setSelected({}); load();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setBulkLoading(false); }
  };

  const load = useCallback(() => {
    api.get(`/envios?sucursal=${suc}`).then(({ data }) => setEnvios(data)).catch(() => {});
    api.get(`/retiros?sucursal=${suc}`).then(({ data }) => setRetiros(data)).catch(() => {});
  }, [suc]);
  useEffect(() => {
    load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const interval = setInterval(onFocus, 30000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(interval);
    };
  }, [load]);
  useEffect(() => { setSelected({}); setSelRet({}); }, [suc]);
  useEffect(() => { api.get("/sucursales").then(({ data }) => setBranches(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/users").then(({ data }) => setRepartidores(data.filter((u) => ((user?.role === "admin" && u.role === "admin") || (["encargado", "encargado_comisionista", "repartidor", "comisionista"].includes(u.role) && u.sucursal === suc))))).catch(() => {});
  }, [suc]);

  const enOrigen = envios.filter((e) => e.sucursal_origen === suc);
  const enDestino = envios.filter((e) => e.sucursal_destino === suc);
  // Un envío es operable en esta sucursal si: está en origen y aún no embarcó,
  // o llegó/está en destino. Los que salieron de aquí (en tránsito hacia otra) NO se muestran.
  const enviosVisibles = envios.filter((e) =>
    (e.sucursal_origen === suc && ["solicitado", "retirado", "depositado_origen"].includes(e.status)) ||
    (e.sucursal_destino === suc && !["solicitado", "retirado", "depositado_origen"].includes(e.status))
  );
  const activos = enviosVisibles.filter((e) => !["entregado", "devuelto", "reasignado"].includes(e.status));

  const today = pyToday();
  const esEntregaHoy = (e) => e.status === "incidente" && e.incidente?.reagendar_fecha && e.incidente.reagendar_fecha <= today;
  const reagendadosHoy = enDestino.filter(esEntregaHoy);
  const desembarcadosHoy = enDestino.filter((e) => e.status === "en_sucursal_destino" && (e.updated_at || "").slice(0, 10) === today);
  const retirosPorAsignar = retiros.filter((r) => r.status !== "completado" && !r.repartidor_id);
  const porRecibirEmbarcar = enOrigen.filter((e) => ["retirado", "solicitado", "depositado_origen"].includes(e.status));
  const porDesembarcar = enDestino.filter((e) => e.status === "en_transito");
  const desembarcadosSinAsignar = enDestino.filter((e) => e.status === "en_sucursal_destino");
  const enReparto = enDestino.filter((e) => ["asignado_repartidor", "en_reparto"].includes(e.status));
  const incidenciasAbiertas = enDestino.filter((e) => ["incidente", "almacenado"].includes(e.status));
  const enviosOrdenados = [...enviosVisibles].sort((a, b) => (esEntregaHoy(b) ? 1 : 0) - (esEntregaHoy(a) ? 1 : 0));

  const advance = async (e) => {
    let next = NEXT_STATUS[e.status];
    if (isPunto && e.status === "en_sucursal_destino") next = "entregado";
    if (!next) return;
    if (next === "entregado") { setComp(e); return; }
    try { await api.post(`/envios/${e.id}/status`, { status: next }); toast.success(`Estado: ${STATUS_META[next].label}`); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const confirmDelivery = async (payload) => {
    try {
      const { data } = await api.post(`/envios/${comp.id}/status`, { status: "entregado", ...payload });
      toast.success("Entregado con comprobante");
      if (!avisoEntregaRemitente(data)) toast.info("Entrega registrada. El envío no tiene WhatsApp del remitente para el aviso automático.");
      setComp(null); load();
    }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const doAssign = async (repartidor_id) => {
    try {
      if (assign.type === "envio") await api.post(`/envios/${assign.item.id}/asignar`, { repartidor_id });
      else if (assign.type === "retiro") await api.put(`/retiros/${assign.item.id}/asignar`, { repartidor_id });
      else if (assign.type === "bulk-envio") {
        const res = await Promise.allSettled(assign.ids.map((id) => api.post(`/envios/${id}/asignar`, { repartidor_id })));
        const ok = res.filter((r) => r.status === "fulfilled").length;
        toast.success(`${ok}/${assign.ids.length} envíos asignados`);
        setAssign(null); setSelected({}); load(); return;
      } else if (assign.type === "bulk-retiro") {
        const res = await Promise.allSettled(assign.ids.map((id) => api.put(`/retiros/${id}/asignar`, { repartidor_id })));
        const ok = res.filter((r) => r.status === "fulfilled").length;
        toast.success(`${ok}/${assign.ids.length} retiros asignados`);
        setAssign(null); setSelRet({}); load(); return;
      }
      toast.success("Repartidor asignado"); setAssign(null); load();
    } catch (e2) { toast.error(apiErr(e2)); }
  };

  return (
    <>
      <Header title="Gestionar sucursal" subtitle="Admin/encargado opera la sucursal y puede repartir" onMenu={() => setMobileOpen(true)}
        action={
          <div className="flex items-center gap-2">
            {user?.role !== "admin" && (
              <button onClick={() => setShowGasto(true)} data-testid="gs-registrar-gasto-btn" className="flex items-center gap-1.5 rounded-lg bg-[#F5A623] text-[#0A1F44] px-3 py-2 text-sm font-bold hover:bg-[#e0951a]"><BadgeDollarSign className="h-4 w-4" /> Gasto</button>
            )}
            {isAdmin
              ? <select value={suc} onChange={(e) => setSuc(e.target.value)} data-testid="gestionar-sucursal-select" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-[#0A1F44] outline-none focus:border-[#3B82F6]">{branches.map((b) => <option key={b.code} value={b.code}>{b.code} · {b.name}{b.activa === false ? " (inactiva)" : ""}</option>)}</select>
              : <span className="rounded-lg bg-[#0A1F44] text-white px-3.5 py-2 text-sm font-bold">{suc}{isPuntoBranch(suc) ? " · Punto Pya'elino" : ""}</span>}
          </div>
        } />
      <main className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Sucursal del día */}
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid="sucursal-del-dia">
          <div className="text-sm font-bold text-[#0A1F44] mb-3">Mi sucursal del día · {suc}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { k: "retiros-asignar", label: "Retiros por asignar", n: retirosPorAsignar.length, c: "text-amber-600" },
              { k: "por-recibir", label: "Por recibir/embarcar", n: porRecibirEmbarcar.length, c: "text-blue-600" },
              { k: "por-desembarcar", label: "En tránsito por desembarcar", n: porDesembarcar.length, c: "text-cyan-600" },
              { k: "desembarcados-sin-asignar", label: "Desembarcados sin asignar", n: desembarcadosSinAsignar.length, c: "text-[#0A1F44]" },
              { k: "en-reparto", label: "En reparto", n: enReparto.length, c: "text-indigo-600" },
              { k: "incidencias-abiertas", label: "Incidencias abiertas", n: incidenciasAbiertas.length, c: "text-rose-600" },
              { k: "reprogramados-hoy", label: "Reprogramados hoy", n: reagendadosHoy.length, c: "text-amber-600" },
            ].map((t) => (
              <div key={t.k} data-testid={`tarea-${t.k}`} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-center">
                <div className={`text-2xl font-extrabold ${t.c}`}>{t.n}</div>
                <div className="text-[11px] text-[#64748B] mt-0.5 leading-tight">{t.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-[#64748B]">Origen = por recibir/embarcar · Destino = por desembarcar, repartir y entregar. Lo que salió de {suc} hacia otra sucursal se gestiona en el destino.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm"><div className="text-xs text-[#64748B] uppercase">Envíos</div><div className="text-2xl font-extrabold text-[#0A1F44]">{enviosVisibles.length}</div></div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm"><div className="text-xs text-[#64748B] uppercase">Activos</div><div className="text-2xl font-extrabold text-[#0A1F44]">{activos.length}</div></div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm"><div className="text-xs text-[#64748B] uppercase">Entregados</div><div className="text-2xl font-extrabold text-emerald-600">{enviosVisibles.length - activos.length}</div></div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm"><div className="text-xs text-[#64748B] uppercase">Retiros</div><div className="text-2xl font-extrabold text-[#0A1F44]">{retiros.length}</div></div>
        </div>

        {/* Prioridades del día */}
        {(reagendadosHoy.length > 0 || desembarcadosHoy.length > 0) && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="prioridades-hoy">
            <div className="text-sm font-bold text-amber-900">Prioridades del día · {suc}</div>
            <div className="mt-1 text-xs text-amber-800 space-y-0.5">
              {reagendadosHoy.length > 0 && <div>• {reagendadosHoy.length} incidencia(s) reprogramada(s) para entregar <b>HOY</b>{isPunto ? "" : " — asigná repartidor primero"}: <span className="font-mono font-semibold">{reagendadosHoy.map((e) => e.code).join(", ")}</span></div>}
              {desembarcadosHoy.length > 0 && <div>• {desembarcadosHoy.length} envío(s) desembarcados hoy {isPunto ? "disponibles para retiro en el punto." : "pendientes de asignar a repartidor."}</div>}
            </div>
          </div>
        )}

        {/* Retiros de la sucursal */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-semibold text-[#0A1F44]"><Truck className="h-4 w-4 text-[#F5A623]" /> Retiros en {suc}</div>
          {selRetIds.length > 0 && (
            <div className="px-4 py-3 bg-amber-50/70 border-b border-amber-100 flex flex-wrap items-center gap-2" data-testid="bulk-retiros-bar">
              <span className="text-sm font-semibold text-[#0A1F44]">{selRetIds.length} retiro{selRetIds.length > 1 ? "s" : ""} seleccionado{selRetIds.length > 1 ? "s" : ""}</span>
              <button onClick={() => setAssign({ type: "bulk-retiro", ids: selRetIds })} data-testid="bulk-asignar-retiros-btn" className="ml-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-[#F5A623] text-white hover:bg-[#e09512]"><UserPlus className="h-4 w-4" /> Asignar a un repartidor</button>
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {retiros.map((r) => (
              <div key={r.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`gs-retiro-${r.id}`}>
                <div className="flex items-center gap-3">
                  {r.status !== "completado" && (
                    <input type="checkbox" checked={!!selRet[r.id]} onChange={() => toggleRet(r.id)} data-testid={`gs-select-retiro-${r.id}`} className="h-4 w-4 rounded border-slate-300 accent-[#F5A623]" />
                  )}
                  <div>
                    <div className="font-semibold text-[#0A1F44] text-sm">{r.cliente_nombre} <span className="text-xs font-normal text-[#64748B]">· {r.direccion}</span></div>
                    <div className="text-xs text-[#64748B]">{r.ciudad}{r.envio_code ? ` · Boleta ${r.envio_code}` : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${{ solicitado: "bg-slate-100 text-slate-700", asignado: "bg-blue-50 text-blue-700", completado: "bg-emerald-50 text-emerald-700" }[r.status]}`}>{r.status}</span>
                  {r.status !== "completado" && (
                    <button onClick={() => setAssign({ type: "retiro", item: r })} data-testid={`gs-assign-retiro-${r.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-[#F5A623] border border-amber-300 rounded-lg px-3 py-1.5 hover:bg-amber-50"><UserPlus className="h-3.5 w-3.5" /> Asignar</button>
                  )}
                </div>
              </div>
            ))}
            {retiros.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#64748B]">No hay retiros en {suc}.</div>}
          </div>
        </div>

        {/* Envíos de la sucursal */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-semibold text-[#0A1F44]"><Store className="h-4 w-4 text-[#F5A623]" /> Envíos de {suc}</div>

          {/* Barra de acciones en bloque */}
          {selectedIds.length > 0 && (
            <div className="px-4 py-3 bg-blue-50/70 border-b border-blue-100 flex flex-wrap items-center gap-2" data-testid="bulk-actions-bar">
              <span className="text-sm font-semibold text-[#0A1F44]">{selectedIds.length} seleccionado{selectedIds.length > 1 ? "s" : ""}</span>
              <div className="flex flex-wrap gap-2 ml-auto">
                {!isPunto && (
                  <>
                    <button onClick={() => bulk("recibir")} disabled={bulkLoading} data-testid="bulk-recibir-btn" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"><PackageCheck className="h-4 w-4 text-slate-600" /> Recibir en sucursal</button>
                    <button onClick={() => bulk("embarcar")} disabled={bulkLoading} data-testid="bulk-embarcar-btn" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"><Ship className="h-4 w-4" /> Embarcar (→ en tránsito)</button>
                  </>
                )}
                <button onClick={() => bulk("desembarcar")} disabled={bulkLoading} data-testid="bulk-desembarcar-btn" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-[#F5A623] text-white hover:bg-[#e09512] disabled:opacity-50"><PackageSearch className="h-4 w-4" /> Desembarcar</button>
                {!isPunto && (
                  <button onClick={() => setAssign({ type: "bulk-envio", ids: selectedIds })} disabled={bulkLoading} data-testid="bulk-asignar-btn" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-[#0A1F44] text-white hover:bg-[#132D5C] disabled:opacity-50"><UserPlus className="h-4 w-4" /> Asignar a un repartidor</button>
                )}
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[#64748B] text-xs uppercase"><tr><th className="px-4 py-3 w-8"></th><th className="text-left font-semibold px-4 py-3">Código</th><th className="text-left font-semibold px-4 py-3">Destinatario</th><th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Ruta</th><th className="text-left font-semibold px-4 py-3">Estado</th><th className="text-right font-semibold px-4 py-3 hidden md:table-cell">Cobro</th><th className="text-right font-semibold px-4 py-3">Acciones</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {enviosOrdenados.map((e) => (
                  <tr key={e.id} className={`hover:bg-slate-50/60 ${selected[e.id] ? "bg-blue-50/50" : ""} ${esEntregaHoy(e) ? "bg-amber-50/70" : ""}`}>
                    <td className="px-4 py-3">
                      {!["entregado", "devuelto", "incidente", "almacenado", "reasignado"].includes(e.status) && (
                        <input type="checkbox" checked={!!selected[e.id]} onChange={() => toggleSel(e.id)} data-testid={`gs-select-${e.code}`} className="h-4 w-4 rounded border-slate-300 accent-[#0A1F44]" />
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono font-bold text-xs">{e.code}{esEntregaHoy(e) && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">ENTREGAR HOY</span>}</td>
                    <td className="px-4 py-3">{e.destinatario}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-xs text-[#64748B]">{e.sucursal_origen} → {e.sucursal_destino}</td>
                    <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-3 text-right hidden md:table-cell font-semibold">{formatGs(e.cobro_destinatario)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {!e.flete_confirmado && e.sucursal_origen === suc && (
                          <button onClick={() => setCf(e)} title="Confirmar flete" data-testid={`gs-flete-${e.code}`} className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-lg px-2 py-1.5 hover:bg-amber-100"><BadgeDollarSign className="h-3.5 w-3.5" /> Flete</button>
                        )}
                        {!isPunto && (!["entregado", "devuelto", "incidente", "almacenado", "reasignado"].includes(e.status) || esEntregaHoy(e)) && !isPuntoBranch(e.sucursal_destino) && (
                          <button onClick={() => setAssign({ type: "envio", item: e })} title="Asignar repartidor" data-testid={`gs-assign-envio-${e.code}`} className={`p-1.5 rounded-lg ${esEntregaHoy(e) ? "text-white bg-[#F5A623] hover:bg-[#e0961a]" : "text-[#F5A623] hover:bg-amber-50"}`}><UserPlus className="h-4 w-4" /></button>
                        )}
                        {isPuntoBranch(e.sucursal_destino) && e.status === "en_sucursal_destino" && (
                          <button onClick={() => window.open(waLink(e.whatsapp, `Hola ${e.destinatario}! Tu encomienda ${e.code} de Pya'elino Courier ya está disponible para retiro en nuestro punto de ${e.ciudad}. Te esperamos. Seguimiento: ${window.location.origin}/track/${e.code}`), "_blank")} title="Avisar retiro disponible por WhatsApp" data-testid={`gs-avisar-retiro-${e.code}`} className="flex items-center gap-1 text-[11px] font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg px-2 py-1.5"><MessageCircle className="h-3.5 w-3.5" /> Avisar retiro</button>
                        )}
                        {["asignado_repartidor", "en_reparto", "en_sucursal_destino"].includes(e.status) && (
                          <button onClick={() => setInc(e)} title="Reportar incidencia" data-testid={`gs-incidencia-${e.code}`} className="p-1.5 rounded-lg text-white bg-rose-500 hover:bg-rose-600"><AlertTriangle className="h-4 w-4" /></button>
                        )}
                        {["incidente", "almacenado"].includes(e.status) && (
                          <button onClick={() => setResol(e)} title="Resolver incidencia" data-testid={`gs-resolver-${e.code}`} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700">Resolver</button>
                        )}
                        {NEXT_STATUS[e.status] && (
                          <button onClick={() => advance(e)} data-testid={`gs-advance-${e.code}`} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-[#0A1F44] text-white hover:bg-[#132D5C]">{(isPunto && e.status === "en_sucursal_destino") ? "Entregado" : STATUS_META[NEXT_STATUS[e.status]].label}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {enviosVisibles.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#64748B]">No hay envíos operables en {suc}.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Asignar repartidor (envío o retiro) */}
      <Dialog open={!!assign} onOpenChange={() => setAssign(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">{assign?.type === "bulk-envio" ? `Asignar ${assign.ids.length} ${assign.ids.length === 1 ? "envío" : "envíos"} a un repartidor` : assign?.type === "bulk-retiro" ? `Asignar ${assign.ids.length} ${assign.ids.length === 1 ? "retiro" : "retiros"} a un repartidor` : `Asignar repartidor${assign?.type === "envio" ? ` · ${assign.item.code}` : ""}`}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {repartidores.map((u) => (
              <button key={u.id} onClick={() => doAssign(u.id)} data-testid={`gs-assign-rep-${u.id}`} className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-[#F5A623] hover:bg-amber-50/40">
                <div className="font-semibold text-[#0A1F44]">{u.name}</div>
                <div className="text-xs text-[#64748B]">{u.role}{u.sucursal ? ` · ${u.sucursal}` : ""}</div>
              </button>
            ))}
            {repartidores.length === 0 && <p className="text-sm text-[#64748B] text-center py-6">No hay repartidores en {suc}. Creálos en Usuarios y Roles con esta sucursal.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Avisar retiro en punto tras desembarcar */}
      <Dialog open={avisar.length > 0} onOpenChange={(o) => { if (!o) setAvisar([]); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Avisar al destinatario</DialogTitle>
            <DialogDescription>{avisar.length} encomienda(s) disponibles para retiro en {suc}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {avisar.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="font-semibold text-[#0A1F44] text-sm truncate">{e.destinatario}</div>
                  <div className="text-xs text-[#64748B] font-mono">{e.code}</div>
                </div>
                <button
                  onClick={() => window.open(waLink(e.whatsapp, `Hola ${e.destinatario}! Tu encomienda ${e.code} de Pya'elino Courier ya está disponible para retiro en nuestro punto de ${e.ciudad}. Te esperamos. Seguimiento: ${window.location.origin}/track/${e.code}`), "_blank")}
                  data-testid={`avisar-retiro-${e.code}`}
                  className="flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg px-3 py-2 shrink-0"
                >
                  <MessageCircle className="h-4 w-4" /> Avisar retiro
                </button>
              </div>
            ))}
          </div>
          <button onClick={() => setAvisar([])} className="w-full rounded-lg border border-slate-300 py-2 text-sm font-medium">Cerrar</button>
        </DialogContent>
      </Dialog>

      <ConfirmFlete envio={cf} onClose={() => setCf(null)} onDone={load} />
      <ComprobanteModal open={!!comp} envio={comp} onClose={() => setComp(null)} onConfirm={confirmDelivery} />
      <IncidenciaModal envio={inc} onClose={() => setInc(null)} onDone={load} />
      <ResolverIncidenciaModal envio={resol} onClose={() => setResol(null)} onDone={load} canAlmacenar />
      <GastoModal open={showGasto} onClose={() => setShowGasto(false)} isAdmin={user?.role === "admin"} onSaved={load} />
    </>
  );
}
